import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, MapPin, Loader2, Check, AlertCircle, Calendar, Clock, Grid3x3, List, Download, Mail, CalendarClock, Eye } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import Layout from '@/components/layout/Layout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { formatClocks, formatTime, formatTimeRange, formatScheduleLabel } from '@/lib/utils';
import { compactLessonLabel } from '@/pages/Calendar/calendarUtils';
import { retryOnCollision } from '@/components/schedule/collisionWarning';
import EmailPreviewConfirm from '@/components/email/EmailPreviewConfirm';

const DAYS = [
  { v: 1, label: 'Montag', short: 'Mo' },
  { v: 2, label: 'Dienstag', short: 'Di' },
  { v: 3, label: 'Mittwoch', short: 'Mi' },
  { v: 4, label: 'Donnerstag', short: 'Do' },
  { v: 5, label: 'Freitag', short: 'Fr' },
  { v: 6, label: 'Samstag', short: 'Sa' },
  { v: 7, label: 'Sonntag', short: 'So' },
];

// ---- Grafik-Ansicht Helfer (wie Stundenplan) ----
const PX_PER_MIN = 1.7;
const PALETTE = [
  { bg: '#f9a8d4', text: '#500724' }, { bg: '#7f1d1d', text: '#fecaca' },
  { bg: '#1e293b', text: '#e2e8f0' }, { bg: '#67e8f9', text: '#083344' },
  { bg: '#6d28d9', text: '#ede9fe' }, { bg: '#166534', text: '#dcfce7' },
  { bg: '#fde047', text: '#422006' }, { bg: '#1d4ed8', text: '#dbeafe' },
  { bg: '#c2410c', text: '#ffedd5' }, { bg: '#0f766e', text: '#ccfbf1' },
  { bg: '#a21caf', text: '#fae8ff' }, { bg: '#84cc16', text: '#1a2e05' },
];
const colorFor = (key) => {
  let h = 0;
  for (const ch of String(key || 'x')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return PALETTE[h % PALETTE.length];
};
const toMin = (hhmm) => parseInt(hhmm.slice(0, 2), 10) * 60 + parseInt(hhmm.slice(3, 5), 10);

const layoutLanes = (items) => {
  const sorted = [...items].sort((a, b) => toMin(a.start_time) - toMin(b.start_time));
  const lanes = [];
  for (const it of sorted) {
    const itStart = toMin(it.start_time);
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      const last = lanes[i][lanes[i].length - 1];
      if (toMin(last.start_time) + last.duration_minutes <= itStart) {
        lanes[i].push(it); it._lane = i; placed = true; break;
      }
    }
    if (!placed) { it._lane = lanes.length; lanes.push([it]); }
  }
  sorted.forEach((it) => { it._lanes = Math.max(1, lanes.length); });
  return sorted;
};

export default function RoomFinder() {
  const [mode, setMode] = useState('list'); // 'list' | 'grid'
  const [day, setDay] = useState(1);
  const [days, setDays] = useState([1, 2, 3, 4, 5]);  // for grid mode
  const [start, setStart] = useState('14:00');
  const [end, setEnd] = useState('17:00');
  const [slotMinutes, setSlotMinutes] = useState(30);
  const [locations, setLocations] = useState([]);
  const [locationFilter, setLocationFilter] = useState('');
  const [result, setResult] = useState(null);
  const [gridResult, setGridResult] = useState(null);
  const [timelineResults, setTimelineResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/locations').then(r => setLocations(r.data)).catch(() => {});
  }, []);

  // Reset result on mode switch to avoid mixing state
  useEffect(() => { setResult(null); setGridResult(null); setTimelineResults(null); }, [mode]);

  const search = async () => {
    setLoading(true);
    try {
      if (mode === 'list') {
        const res = await api.post('/locations/room-finder', {
          day_of_week: day, start_time: start, end_time: end,
          location_id: locationFilter || null, only_active: true,
        });
        setResult(res.data);
      } else if (mode === 'timeline') {
        const resList = await Promise.all(days.map(d => api.post('/locations/room-finder', {
          day_of_week: d, start_time: start, end_time: end,
          location_id: locationFilter || null, only_active: true,
        })));
        setTimelineResults(resList.map(r => r.data));
      } else {
        const res = await api.post('/locations/room-finder/grid', {
          days_of_week: days, start_time: start, end_time: end,
          slot_minutes: slotMinutes, location_id: locationFilter || null, only_active: true,
        });
        setGridResult(res.data);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Suche fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const toggleDay = (v) => {
    setDays(prev => prev.includes(v) ? prev.filter(x => x !== v) : [...prev, v].sort());
  };

  return (
    <Layout>
      <div className="p-6 md:p-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-1" data-testid="room-finder-title">Raumfinder</h1>
            <p className="text-slate-600 text-sm">Finde freie Räume — Einzel-Slot oder Mehrslot-Grid.</p>
          </div>
          {/* Mode toggle */}
          <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
            <button
              onClick={() => setMode('list')}
              data-testid="rf-mode-list"
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 ${mode === 'list' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <List className="h-4 w-4" /> Liste
            </button>
            <button
              onClick={() => setMode('timeline')}
              data-testid="rf-mode-timeline"
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 ${mode === 'timeline' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <CalendarClock className="h-4 w-4" /> Grafik
            </button>
            <button
              onClick={() => setMode('grid')}
              data-testid="rf-mode-grid"
              className={`px-3 py-1.5 rounded-md text-sm flex items-center gap-2 ${mode === 'grid' ? 'bg-slate-900 text-white' : 'text-slate-600 hover:bg-slate-100'}`}
            >
              <Grid3x3 className="h-4 w-4" /> Grid
            </button>
          </div>
        </div>

        {/* Search Form */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 mb-6">
          <div className="mb-4 text-xs text-slate-500 leading-relaxed bg-slate-50 border border-slate-100 rounded-md px-3 py-2">
            {mode === 'list' ? (
              <>
                <strong className="text-slate-700">Liste</strong>: Sucht freie Räume an <strong>einem</strong> Wochentag in einem zusammenhängenden Zeitfenster (z.B. Montag 14–17 Uhr).
              </>
            ) : mode === 'timeline' ? (
              <>
                <strong className="text-slate-700">Grafik</strong>: Zeigt die Tagesbelegung aller Räume als Zeitstrahl (wie im Stundenplan) — auch für <strong>mehrere Wochentage nebeneinander</strong>. Das gesuchte Zeitfenster wird grün hinterlegt.
              </>
            ) : (
              <>
                <strong className="text-slate-700">Grid</strong>: Sucht freie Räume an <strong>mehreren</strong> Wochentagen gleichzeitig und zerlegt das Zeitfenster in kleine Slots.{' '}
                <strong className="text-slate-700">Slot-Größe</strong> = Länge eines Einzelslots in Minuten (z.B. 30 → 14:00, 14:30, 15:00 …).
              </>
            )}
          </div>
          {mode === 'list' ? (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Calendar className="h-3 w-3" /> Wochentag
                </Label>
                <Select value={String(day)} onValueChange={v => setDay(Number(v))}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="rf-day"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {DAYS.map(d => <SelectItem key={d.v} value={String(d.v)}>{d.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Von
                </Label>
                <Input type="time" value={start} onChange={e => setStart(e.target.value)} className="mt-1" data-testid="rf-start" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                  <Clock className="h-3 w-3" /> Bis
                </Label>
                <Input type="time" value={end} onChange={e => setEnd(e.target.value)} className="mt-1" data-testid="rf-end" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Standort (optional)</Label>
                <Select value={locationFilter || '_all'} onValueChange={v => setLocationFilter(v === '_all' ? '' : v)}>
                  <SelectTrigger className="mt-1 bg-white" data-testid="rf-location"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_all">Alle Standorte</SelectItem>
                    {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                <Button onClick={search} disabled={loading} className="w-full bg-brand-red hover:bg-brand-red/90" data-testid="rf-search-btn">
                  {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                  Suchen
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1 mb-2">
                  <Calendar className="h-3 w-3" /> Wochentage
                </Label>
                <div className="flex gap-2 flex-wrap">
                  {DAYS.map(d => (
                    <button key={d.v} onClick={() => toggleDay(d.v)}
                            data-testid={`rf-grid-day-${d.v}`}
                            className={`px-4 py-2 rounded-md border text-sm font-medium transition-all ${
                              days.includes(d.v) ? 'bg-brand-red text-white border-brand-red' : 'bg-white text-slate-700 border-slate-300 hover:border-slate-400'
                            }`}>
                      {d.short}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Von
                  </Label>
                  <Input type="time" value={start} onChange={e => setStart(e.target.value)} className="mt-1" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500 flex items-center gap-1">
                    <Clock className="h-3 w-3" /> Bis
                  </Label>
                  <Input type="time" value={end} onChange={e => setEnd(e.target.value)} className="mt-1" />
                </div>
                {mode === 'grid' && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Slot-Größe</Label>
                  <Select value={String(slotMinutes)} onValueChange={v => setSlotMinutes(Number(v))}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="rf-slot-minutes"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[15, 30, 45, 60, 90].map(m => <SelectItem key={m} value={String(m)}>{m} Min</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                )}
                <div>
                  <Label className="text-xs uppercase tracking-wide text-slate-500">Standort</Label>
                  <Select value={locationFilter || '_all'} onValueChange={v => setLocationFilter(v === '_all' ? '' : v)}>
                    <SelectTrigger className="mt-1 bg-white" data-testid="rf-location-grid"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all">Alle Standorte</SelectItem>
                      {locations.map(l => <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-end">
                  <Button onClick={search} disabled={loading || days.length === 0} className="w-full bg-brand-red hover:bg-brand-red/90"
                          data-testid={mode === 'grid' ? 'rf-search-grid-btn' : 'rf-search-timeline-btn'}>
                    {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Search className="h-4 w-4 mr-2" />}
                    {mode === 'grid' ? 'Grid anzeigen' : 'Suchen'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Result */}
        {mode === 'list' && result && <ListResult result={result} navigate={navigate} />}
        {mode === 'timeline' && timelineResults && (
          <TimelineResult
            key={timelineResults.map(r => r.request.day_of_week).join('-')}
            results={timelineResults}
            navigate={navigate}
            onRefresh={search}
            params={{ days_of_week: days, start_time: start, end_time: end, location_id: locationFilter || null, only_active: true }}
          />
        )}
        {mode === 'grid' && gridResult && (
          <GridResult
            data={gridResult}
            navigate={navigate}
            params={{ days_of_week: days, start_time: start, end_time: end, slot_minutes: slotMinutes, location_id: locationFilter || null }}
          />
        )}
        {!result && !gridResult && !timelineResults && !loading && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center text-slate-500">
            {mode === 'grid'
              ? 'Wähle Wochentage, Zeitfenster, Slot-Größe — dann auf „Grid anzeigen" klicken.'
              : 'Wähle Wochentag und Zeitfenster, dann auf „Suchen" klicken.'}
          </div>
        )}
      </div>
    </Layout>
  );
}


// -------- List mode result --------
export function ListResult({ result, navigate }) {
  const [showBusy, setShowBusy] = useState(false);
  return (
    <div className="space-y-4">
      <div className="text-sm text-slate-600 flex items-center gap-2">
        <span className="font-semibold">{result.total_rooms}</span> Räume gescannt:
        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">{result.available.length} frei</span>
        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">{result.busy.length} belegt</span>
      </div>
      <div className="bg-white rounded-xl border border-slate-200">
        <div className="px-6 py-3 border-b border-slate-100 bg-emerald-50/40 rounded-t-xl">
          <div className="flex items-center gap-2 text-emerald-800 font-semibold">
            <Check className="h-5 w-5" /> Freie Räume ({result.available.length})
          </div>
        </div>
        {result.available.length === 0 ? (
          <p className="p-6 text-center text-slate-500 italic">Keine freien Räume in diesem Zeitfenster.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {result.available.map(r => (
              <li key={r.room_id} className="px-6 py-3 flex items-center gap-3 hover:bg-slate-50">
                <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                <button onClick={() => navigate(`/locations/${r.location_id}`)} className="text-left flex-1 min-w-0 group">
                  <div className="font-medium text-slate-800 truncate group-hover:text-brand-red">{r.room_name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1">
                    <MapPin className="h-3 w-3" /> {r.location_name}
                    {r.room_description && <span className="text-slate-400">· {r.room_description}</span>}
                  </div>
                </button>
                {r.wheelchair_accessible && <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">♿</span>}
              </li>
            ))}
          </ul>
        )}
      </div>
      {result.busy.length > 0 && (
        <button
          type="button"
          className="text-sm font-semibold text-slate-700 underline"
          data-testid="rf-show-busy"
          onClick={() => setShowBusy((open) => !open)}
        >
          {showBusy ? 'Belegte Räume ausblenden' : `Belegte Räume anzeigen (${result.busy.length})`}
        </button>
      )}
      {showBusy && result.busy.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200">
          <div className="px-6 py-3 border-b border-slate-100 bg-red-50/40 rounded-t-xl">
            <div className="flex items-center gap-2 text-red-800 font-semibold">
              <AlertCircle className="h-5 w-5" /> Belegte Räume ({result.busy.length})
            </div>
          </div>
          <ul className="divide-y divide-slate-100">
            {result.busy.map(r => (
              <li key={r.room_id} className="px-6 py-3">
                <button onClick={() => navigate(`/locations/${r.location_id}`)} className="text-left w-full group">
                  <div className="font-medium text-slate-800 group-hover:text-brand-red">{r.room_name}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                    <MapPin className="h-3 w-3" /> {r.location_name}
                  </div>
                </button>
                <div className="ml-4 mt-1 space-y-1">
                  {r.conflicts.map(c => (
                    <div key={c.course_id} className="text-xs flex items-center gap-2 text-slate-600">
                      <Clock className="h-3 w-3 text-red-400" />
                      <span className="font-mono">{formatTime(c.start_time)}</span>
                      <span className="text-slate-400">+ {c.duration_minutes} Min</span>
                      <span className="truncate">{compactLessonLabel(formatScheduleLabel(c.course_name))}</span>
                      {c.teacher && <span className="text-slate-400">· {c.teacher}</span>}
                    </div>
                  ))}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


// -------- Timeline (Grafik) mode result — wie Stundenplan, mehrere Tage nebeneinander --------
const locPrio = (n) => ((n || '').startsWith('Musik Akademie') ? 0 : 1);

function TimelineResult({ results, navigate, params, onRefresh }) {
  const [showAll, setShowAll] = useState(results.length === 1);
  const [booking, setBooking] = useState(null); // { room, day, time }
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await api.post('/locations/room-finder/timeline/pdf', params, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'raumfinder_woche.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (e) {
      toast.error('PDF-Erstellung fehlgeschlagen');
    } finally { setPdfLoading(false); }
  };

  const openEmailPreview = async () => {
    const recipients = emailRecipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) return toast.error('Mindestens 1 Empfänger angeben');
    setEmailSending(true);
    try {
      const res = await api.post('/locations/room-finder/timeline/email/preview', {
        ...params, recipients, message: emailMessage || null,
      });
      setEmailPreview({ ...res.data, recipients, message: emailMessage || null });
      setEmailOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Voransicht fehlgeschlagen');
    } finally { setEmailSending(false); }
  };

  const confirmEmail = async (routing = {}) => {
    if (!emailPreview?.preview_token) return;
    setEmailSending(true);
    try {
      const res = await api.post('/locations/room-finder/timeline/email', {
        ...params,
        recipients: emailPreview.recipients,
        message: emailPreview.message,
        preview_token: emailPreview.preview_token,
        ...routing,
      });
      toast.success(`E-Mail an ${res.data.sent}/${res.data.total} Empfänger gesendet`);
      setEmailPreview(null);
      setEmailRecipients('');
      setEmailMessage('');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Versand fehlgeschlagen — bitte SMTP-Konfiguration unter "E-Mail" prüfen');
    } finally { setEmailSending(false); }
  };

  const deleteReservation = async (b) => {
    if (!window.confirm(`Einmalige Belegung „${formatScheduleLabel(b.course_name)}" löschen?`)) return;
    try {
      await api.delete(`/locations/room-reservations/${b.reservation_id}`);
      toast.success('Reservierung gelöscht');
      onRefresh?.();
    } catch { toast.error('Löschen fehlgeschlagen'); }
  };

  // Globaler Zeitbereich über alle Tage (Suchfenster + Buchungen), auf volle Stunden gerundet
  let minM = Infinity, maxM = -Infinity;
  for (const res of results) {
    minM = Math.min(minM, toMin(res.request.start_time));
    maxM = Math.max(maxM, toMin(res.request.end_time));
    for (const r of [...res.available, ...res.busy]) {
      for (const b of (r.bookings || [])) {
        const s = toMin(b.start_time);
        minM = Math.min(minM, s);
        maxM = Math.max(maxM, s + b.duration_minutes);
      }
    }
  }
  const startH = Math.max(0, Math.floor(minM / 60));
  const endH = Math.min(23, Math.ceil(maxM / 60));
  const hours = [];
  for (let h = startH; h <= endH; h++) hours.push(h);
  const dayStart = startH * 60;
  const gridHeight = hours.length * 60 * PX_PER_MIN;

  // Pro Tag: Räume filtern/sortieren (Musik Akademie zuerst) und nach Standort gruppieren
  const dayGroups = results.map((res) => {
    const rooms = [...res.available.map(r => ({ ...r, free: true })), ...res.busy.map(r => ({ ...r, free: false }))]
      .filter(r => showAll || (r.bookings || []).length > 0)
      .sort((a, b) => (locPrio(a.location_name) - locPrio(b.location_name))
        || (a.location_name + a.room_name).localeCompare(b.location_name + b.room_name));
    const locs = [];
    for (const r of rooms) {
      let g = locs.find(l => l.name === r.location_name);
      if (!g) { g = { name: r.location_name, rooms: [] }; locs.push(g); }
      g.rooms.push({ ...r, lanes: layoutLanes(r.bookings || []) });
    }
    return {
      day: res.request.day_of_week,
      label: DAYS.find(d => d.v === res.request.day_of_week)?.label || '',
      winStart: toMin(res.request.start_time),
      winEnd: toMin(res.request.end_time),
      locs,
      roomCount: rooms.length,
      freeCount: res.available.length,
      busyCount: res.busy.length,
    };
  });

  const totalFree = dayGroups.reduce((a, g) => a + g.freeCount, 0);
  const totalBusy = dayGroups.reduce((a, g) => a + g.busyCount, 0);
  const req = results[0].request;

  const renderRoomColumn = (r, g) => (
    <div key={r.room_id} className="w-[150px] shrink-0 relative border-r border-slate-100 cursor-copy"
         style={{ height: gridHeight }} data-testid={`rf-timeline-col-${g.day}-${r.room_id}`}
         title="Klick auf eine freie Zeit: Raum belegen / Unterricht anlegen"
         onClick={(ev) => {
           const rect = ev.currentTarget.getBoundingClientRect();
           let m = dayStart + (ev.clientY - rect.top) / PX_PER_MIN;
           m = Math.max(0, Math.round(m / 15) * 15);
           const t = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
           setBooking({ room: r, day: g.day, time: t });
         }}>
      {hours.map((h, i) => (
        <div key={h} className="absolute left-0 right-0 border-t border-slate-100" style={{ top: i * 60 * PX_PER_MIN }} />
      ))}
      {/* Suchfenster-Markierung */}
      <div className="absolute left-0 right-0 bg-emerald-100/50 border-y-2 border-emerald-400/60 pointer-events-none"
           style={{ top: (g.winStart - dayStart) * PX_PER_MIN, height: (g.winEnd - g.winStart) * PX_PER_MIN }} />
      {r.lanes.map((b, i) => {
        const isResv = b.type === 'reservation';
        const col = isResv ? { bg: '#475569', text: '#f1f5f9' } : colorFor(b.teacher_id);
        const bs = toMin(b.start_time);
        const width = 100 / (b._lanes || 1);
        return (
          <div key={`${b.course_id || b.reservation_id}-${i}`}
               className={`absolute rounded-sm overflow-hidden shadow-sm cursor-pointer ${isResv ? 'border border-dashed border-white/60' : 'border border-black/10'}`}
               data-testid={isResv ? `rf-timeline-resv-${b.reservation_id}` : `rf-timeline-block-${b.course_id}`}
               onClick={(ev) => {
                 ev.stopPropagation();
                 if (isResv) deleteReservation(b);
                 else navigate(`/courses/${b.course_id}`);
               }}
               title={isResv
                 ? `${compactLessonLabel(formatScheduleLabel(b.course_name))}\n${formatTime(b.start_time)} + ${b.duration_minutes} Min\nKlick: Reservierung löschen`
                 : `${compactLessonLabel(formatScheduleLabel(b.course_name))}\n${b.teacher || ''}\n${formatTime(b.start_time)} + ${b.duration_minutes} Min\nKlick: Veranstaltung öffnen`}
               style={{
                 top: (bs - dayStart) * PX_PER_MIN,
                 height: Math.max(26, b.duration_minutes * PX_PER_MIN - 2),
                 left: `${(b._lane || 0) * width}%`, width: `calc(${width}% - 2px)`,
                 background: col.bg, color: col.text,
               }}>
            <div className="px-1 py-0.5 text-[10px] font-bold leading-tight truncate">
              {formatTime(b.start_time)} {b.teacher || compactLessonLabel(formatScheduleLabel(b.course_name))}
            </div>
            <div className="px-1 text-[9px] leading-[1.25] opacity-90 truncate">{compactLessonLabel(formatScheduleLabel(b.course_name))}</div>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-3">
      <div className="text-sm text-slate-600 flex items-center gap-2 flex-wrap">
        <span className="font-semibold">{dayGroups.map(g => g.label).join(', ')}</span>
        <span className="font-mono text-xs bg-emerald-50 text-emerald-800 border border-emerald-200 rounded px-2 py-0.5">{formatTimeRange(req.start_time, req.end_time)}</span>
        <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-semibold">{totalFree} frei</span>
        <span className="px-2 py-0.5 rounded-full bg-red-50 text-red-700 text-xs font-semibold">{totalBusy} belegt</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-500 ml-auto cursor-pointer select-none">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
                 data-testid="rf-timeline-all-rooms-toggle" className="accent-brand-red" />
          Alle Räume anzeigen (auch ohne Belegung)
        </label>
        <Button onClick={downloadPDF} disabled={pdfLoading} variant="outline" size="sm" data-testid="rf-timeline-pdf-btn"
                className="border-slate-300 h-7">
          {pdfLoading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Download className="h-3.5 w-3.5 mr-1.5" />}
          PDF
        </Button>
        <Button onClick={() => setEmailOpen(true)} variant="outline" size="sm" data-testid="rf-timeline-email-btn"
                className="border-brand-red/20 text-brand-red hover:bg-brand-red/5 h-7">
          <Mail className="h-3.5 w-3.5 mr-1.5" /> E-Mail
        </Button>
      </div>

      {/* E-Mail Modal */}
      {emailOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEmailOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Wochenbelegung per E-Mail senden</h3>
            <p className="text-xs text-slate-500 mb-4">Anhang: PDF mit der aktuellen Wochen-Grafikansicht.</p>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Empfänger (Kommagetrennt)</Label>
            <Input value={emailRecipients} onChange={e => setEmailRecipients(e.target.value)}
                   placeholder="kollege@musikschule.de"
                   className="mt-1 mb-3" data-testid="rf-timeline-email-recipients" autoFocus />
            <Label className="text-xs uppercase tracking-wide text-slate-500">Begleittext (optional)</Label>
            <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)}
                      rows={3}
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEmailOpen(false)}>Abbrechen</Button>
              <Button onClick={openEmailPreview} disabled={emailSending} className="bg-brand-red hover:bg-brand-red/90"
                      data-testid="rf-timeline-email-send-btn">
                {emailSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Voransicht
              </Button>
            </div>
          </div>
        </div>
      )}

      <EmailPreviewConfirm
        open={!!emailPreview}
        onClose={() => setEmailPreview(null)}
        onConfirm={confirmEmail}
        confirming={emailSending}
        title="Wochenbelegung — Voransicht"
        description="Bitte Begleittext prüfen. PDF-Anhang wird mitgesendet. Versand erst nach Bestätigung."
        to={emailPreview?.recipient}
        recipients={emailPreview?.recipients}
        subject={emailPreview?.subject}
        html={emailPreview?.html}
        attachmentNote={emailPreview?.attachment_note}
        sampleNote={emailPreview?.is_sample
          ? `Beispiel für 1 von ${emailPreview.eligible_count} Empfängern.`
          : null}
        confirmLabel={`An ${emailPreview?.eligible_count ?? 0} Empfänger senden`}
        testId="rf-timeline-email-preview"
      />

      {booking && (
        <BookingDialog booking={booking} onClose={() => setBooking(null)}
                       onBooked={() => { setBooking(null); onRefresh?.(); }} />
      )}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="rf-timeline">
        <div className="overflow-x-auto">
          <div className="inline-block min-w-full">
            {/* Kopfzeilen: Tag > Standorte > Räume */}
            <div className="flex sticky top-0 z-10">
              <div className="w-12 shrink-0 border-b border-r border-slate-200 bg-slate-50" />
              {dayGroups.map((g) => (
                <div key={g.day} style={{ width: Math.max(1, g.roomCount) * 150 }}
                     className="shrink-0 border-r-2 border-slate-300">
                  <div className="text-center text-xs font-bold text-white bg-brand-red border-b border-brand-red py-1 truncate px-1"
                       data-testid={`rf-timeline-day-header-${g.day}`}>
                    {g.label}
                  </div>
                  {g.roomCount === 0 ? (
                    <div className="text-center text-[11px] text-slate-400 italic bg-slate-50 border-b border-slate-200 py-[26px]">keine Belegung</div>
                  ) : (
                    <div className="flex">
                      {g.locs.map((loc) => (
                        <div key={loc.name} style={{ width: loc.rooms.length * 150 }} className="shrink-0">
                          <div className="text-center text-xs font-bold text-slate-700 bg-slate-100 border-b border-r border-slate-200 py-1 truncate px-1">
                            {loc.name}
                          </div>
                          <div className="flex">
                            {loc.rooms.map((r) => (
                              <button key={r.room_id}
                                      onClick={() => navigate(`/locations/${r.location_id}`)}
                                      data-testid={`rf-timeline-room-${g.day}-${r.room_id}`}
                                      title={r.free ? 'Frei im Suchfenster — Standort öffnen' : 'Belegt im Suchfenster — Standort öffnen'}
                                      className={`w-[150px] shrink-0 text-center text-[11px] font-semibold border-b border-r border-slate-200 py-1 truncate px-1 hover:underline ${
                                        r.free ? 'bg-emerald-50 text-emerald-800' : 'bg-red-50 text-red-800'}`}>
                                {r.free ? <Check className="inline h-3 w-3 mr-0.5 -mt-0.5" /> : <AlertCircle className="inline h-3 w-3 mr-0.5 -mt-0.5" />}
                                {r.room_name}
                              </button>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* Zeitraster + Blöcke */}
            <div className="flex">
              <div className="w-12 shrink-0 relative border-r border-slate-200" style={{ height: gridHeight }}>
                {hours.map((h, i) => (
                  <div key={h} className="absolute left-0 right-0 text-[10px] text-slate-400 text-right pr-1 -translate-y-1/2"
                       style={{ top: i * 60 * PX_PER_MIN }}>
                    {String(h).padStart(2, '0')}:00
                  </div>
                ))}
              </div>
              {dayGroups.map((g) => (
                <div key={g.day} className="flex shrink-0 border-r-2 border-slate-300" style={{ width: Math.max(1, g.roomCount) * 150 }}>
                  {g.roomCount === 0 ? (
                    <div className="w-[150px] shrink-0 relative bg-slate-50/40" style={{ height: gridHeight }} />
                  ) : (
                    g.locs.map((loc) => loc.rooms.map((r) => renderRoomColumn(r, g)))
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <p className="text-[11px] text-slate-400">
        Grüner Bereich = gesuchtes Zeitfenster · Farben je Lehrkraft · Grau gestrichelt = einmalige Belegung (Klick löscht) · Klick auf freie Zeit = Raum belegen / Unterricht anlegen · Klick auf Block = Veranstaltung
      </p>
    </div>
  );
}


// -------- Buchungsdialog: Einmalige Belegung ODER Unterricht anlegen --------
function BookingDialog({ booking, onClose, onBooked }) {
  const nextDateFor = (weekday) => {
    const t = new Date();
    const cur = t.getDay() === 0 ? 7 : t.getDay();
    const x = new Date(t);
    x.setDate(t.getDate() + ((weekday - cur + 7) % 7));
    return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
  };

  const [tab, setTab] = useState('once');
  const [saving, setSaving] = useState(false);
  const [subjects, setSubjects] = useState([]);
  const [teachers, setTeachers] = useState([]);
  // Einmalig
  const [title, setTitle] = useState('');
  const [resDate, setResDate] = useState(() => nextDateFor(booking.day));
  const [resTime, setResTime] = useState(booking.time);
  const [resDuration, setResDuration] = useState(60);
  const [notes, setNotes] = useState('');
  // Unterricht
  const [courseName, setCourseName] = useState('');
  const [subjectId, setSubjectId] = useState('');
  const [courseType, setCourseType] = useState('');
  const [maxCapacity, setMaxCapacity] = useState(8);
  const [teacherId, setTeacherId] = useState('');
  const [courseDuration, setCourseDuration] = useState(45);

  useEffect(() => {
    api.get('/subjects', { params: { limit: 100 } }).then(r => setSubjects(r.data)).catch(() => {});
    api.get('/teachers', { params: { limit: 200 } }).then(r => setTeachers(Array.isArray(r.data) ? r.data : (r.data.items || []))).catch(() => {});
  }, []);

  const dayLabel = DAYS.find(d => d.v === booking.day)?.label || '';

  const saveOnce = async () => {
    if (!title.trim()) return toast.error('Bitte Titel angeben');
    setSaving(true);
    try {
      const payload = {
        title, date: resDate, start_time: resTime, duration_minutes: resDuration, notes: notes || null,
      };
      const saved = await retryOnCollision(
        () => api.post(`/locations/rooms/${booking.room.room_id}/reservations`, payload),
        () => api.post(`/locations/rooms/${booking.room.room_id}/reservations`, { ...payload, allow_collisions: true }),
      );
      if (!saved) return;
      toast.success(`Raum „${booking.room.room_name}" am ${resDate.split('-').reverse().join('.')} um ${resTime} belegt`);
      onBooked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Belegung fehlgeschlagen');
    } finally { setSaving(false); }
  };

  const saveCourse = async () => {
    if (!subjectId) return toast.error('Bitte zuerst das Fach auswählen');
    if (!courseType) return toast.error('Bitte die Unterrichtsform auswählen');
    setSaving(true);
    try {
      const subj = subjects.find(s => s.id === subjectId);
      const res = await api.post('/courses', {
        name: courseName.trim() || `${subj?.name || 'Unterricht'} ${dayLabel} ${booking.time}`,
        subject_id: subjectId,
        teacher_id: teacherId || null,
        room_id: booking.room.room_id,
        course_type: courseType,
        duration_minutes: courseDuration,
        max_capacity: courseType === 'group' ? maxCapacity : 1,
        day_of_week: booking.day,
        start_time: booking.time,
        status: 'active',
      });
      toast.success(`Unterricht „${res.data.name}" angelegt — Teilnehmer können jetzt zugeordnet werden`);
      onBooked();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Anlegen fehlgeschlagen');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="rf-booking-dialog">
        <h3 className="text-lg font-semibold mb-0.5">Zeit belegen</h3>
        <p className="text-xs text-slate-500 mb-4">
          <span className="font-semibold text-slate-700">{booking.room.room_name}</span> · {booking.room.location_name} · {dayLabel} ab {booking.time}
        </p>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5 mb-4 w-full">
          <button onClick={() => setTab('once')} data-testid="rf-booking-tab-once"
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm ${tab === 'once' ? 'bg-white shadow font-semibold text-slate-900' : 'text-slate-500'}`}>
            Einmalige Belegung
          </button>
          <button onClick={() => setTab('course')} data-testid="rf-booking-tab-course"
                  className={`flex-1 px-3 py-1.5 rounded-md text-sm ${tab === 'course' ? 'bg-white shadow font-semibold text-slate-900' : 'text-slate-500'}`}>
            Unterricht anlegen
          </button>
        </div>

        {tab === 'once' ? (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Titel *</Label>
              <Input value={title} onChange={e => setTitle(e.target.value)} placeholder="z.B. Vorspiel-Probe, Elternabend…"
                     className="mt-1" data-testid="rf-booking-title" autoFocus />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Datum</Label>
                <Input type="date" value={resDate} onChange={e => setResDate(e.target.value)} className="mt-1" data-testid="rf-booking-date" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Von</Label>
                <Input type="time" value={resTime} onChange={e => setResTime(e.target.value)} className="mt-1" data-testid="rf-booking-time" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Dauer</Label>
                <Select value={String(resDuration)} onValueChange={v => setResDuration(Number(v))}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[30, 45, 60, 90, 120, 180, 240].map(m => <SelectItem key={m} value={String(m)}>{m} Min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Notiz (optional)</Label>
              <Input value={notes} onChange={e => setNotes(e.target.value)} className="mt-1" />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button onClick={saveOnce} disabled={saving} className="bg-brand-red hover:bg-brand-red/90" data-testid="rf-booking-save-once">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Belegen
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">1. Fach *</Label>
              <Select value={subjectId || undefined} onValueChange={setSubjectId}>
                <SelectTrigger className="mt-1 bg-white" data-testid="rf-booking-subject"><SelectValue placeholder="Fach auswählen…" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {subjects.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className={!subjectId ? 'opacity-50 pointer-events-none' : ''}>
              <Label className="text-xs uppercase tracking-wide text-slate-500">2. Unterrichtsform *</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                <button type="button" onClick={() => setCourseType('individual')} data-testid="rf-booking-type-individual"
                        className={`px-3 py-2 rounded-md border text-sm ${courseType === 'individual'
                          ? 'border-brand-red bg-brand-red/5 text-brand-red font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  Einzelunterricht
                </button>
                <button type="button" onClick={() => setCourseType('group')} data-testid="rf-booking-type-group"
                        className={`px-3 py-2 rounded-md border text-sm ${courseType === 'group'
                          ? 'border-brand-red bg-brand-red/5 text-brand-red font-semibold' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                  Gruppenunterricht
                </button>
              </div>
            </div>
            {courseType === 'group' && (
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Max. Teilnehmer</Label>
                <Input type="number" min={2} max={50} value={maxCapacity}
                       onChange={e => setMaxCapacity(Math.max(2, Number(e.target.value) || 2))}
                       className="mt-1" data-testid="rf-booking-capacity" />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase tracking-wide text-slate-500">Lehrkraft (optional)</Label>
              <Select value={teacherId || undefined} onValueChange={setTeacherId}>
                <SelectTrigger className="mt-1 bg-white" data-testid="rf-booking-teacher"><SelectValue placeholder="Lehrkraft auswählen…" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Bezeichnung (optional)</Label>
                <Input value={courseName} onChange={e => setCourseName(e.target.value)} placeholder="automatisch" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-slate-500">Dauer</Label>
                <Select value={String(courseDuration)} onValueChange={v => setCourseDuration(Number(v))}>
                  <SelectTrigger className="mt-1 bg-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[30, 45, 60, 90].map(m => <SelectItem key={m} value={String(m)}>{m} Min</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 bg-slate-50 border border-slate-100 rounded px-2 py-1.5">
              Wöchentlicher Unterricht: {dayLabel}, {booking.time} Uhr, Raum {booking.room.room_name}. Teilnehmer werden anschließend über die Kurs-Detailseite angemeldet.
            </p>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
              <Button onClick={saveCourse} disabled={saving} className="bg-brand-red hover:bg-brand-red/90" data-testid="rf-booking-save-course">
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Unterricht anlegen
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


// -------- Grid mode result --------
function GridResult({ data, navigate, params }) {
  const [emailOpen, setEmailOpen] = useState(false);
  const [emailRecipients, setEmailRecipients] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailPreview, setEmailPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);

  const downloadPDF = async () => {
    setPdfLoading(true);
    try {
      const res = await api.post('/locations/room-finder/grid/pdf', params, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'raumfinder.pdf';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'PDF-Erstellung fehlgeschlagen');
    } finally { setPdfLoading(false); }
  };

  const openEmailPreview = async () => {
    const recipients = emailRecipients.split(/[,;\s]+/).map(s => s.trim()).filter(Boolean);
    if (recipients.length === 0) return toast.error('Mindestens 1 Empfänger angeben');
    setEmailSending(true);
    try {
      const res = await api.post('/locations/room-finder/grid/email/preview', {
        ...params, recipients, message: emailMessage || null,
      });
      setEmailPreview({ ...res.data, recipients, message: emailMessage || null });
      setEmailOpen(false);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Voransicht fehlgeschlagen');
    } finally { setEmailSending(false); }
  };

  const confirmEmail = async (routing = {}) => {
    if (!emailPreview?.preview_token) return;
    setEmailSending(true);
    try {
      const res = await api.post('/locations/room-finder/grid/email', {
        ...params,
        recipients: emailPreview.recipients,
        message: emailPreview.message,
        preview_token: emailPreview.preview_token,
        ...routing,
      });
      toast.success(`E-Mail an ${res.data.sent}/${res.data.total} Empfänger gesendet`);
      setEmailPreview(null);
      setEmailRecipients('');
      setEmailMessage('');
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Versand fehlgeschlagen — bitte SMTP-Konfiguration unter "E-Mail" prüfen');
    } finally { setEmailSending(false); }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="text-sm text-slate-600">
          <span className="font-semibold">{data.total_rooms}</span> Räume · {data.slots.length} Slots à {data.slot_minutes} Min · {data.days.length} Tage
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={downloadPDF} disabled={pdfLoading} variant="outline" data-testid="rf-pdf-btn"
                  className="border-slate-300">
            {pdfLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
            PDF
          </Button>
          <Button onClick={() => setEmailOpen(true)} variant="outline" data-testid="rf-email-btn"
                  className="border-brand-red/20 text-brand-red hover:bg-brand-red/5">
            <Mail className="h-4 w-4 mr-2" /> Per E-Mail senden
          </Button>
        </div>
      </div>

      {/* E-Mail Modal */}
      {emailOpen && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setEmailOpen(false)}>
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold mb-1">Raumbelegung per E-Mail senden</h3>
            <p className="text-xs text-slate-500 mb-4">Anhang: PDF mit dem aktuellen Grid.</p>
            <Label className="text-xs uppercase tracking-wide text-slate-500">Empfänger (Kommagetrennt)</Label>
            <Input value={emailRecipients} onChange={e => setEmailRecipients(e.target.value)}
                   placeholder="kollege@musikschule.de, leitung@musikschule.de"
                   className="mt-1 mb-3" data-testid="rf-email-recipients" autoFocus />
            <Label className="text-xs uppercase tracking-wide text-slate-500">Begleittext (optional)</Label>
            <textarea value={emailMessage} onChange={e => setEmailMessage(e.target.value)}
                      rows={3}
                      placeholder="Anbei die Wochenbelegung für KW XX…"
                      className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-md text-sm" />
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setEmailOpen(false)}>Abbrechen</Button>
              <Button onClick={openEmailPreview} disabled={emailSending} className="bg-brand-red hover:bg-brand-red/90"
                      data-testid="rf-email-send-btn">
                {emailSending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
                Voransicht
              </Button>
            </div>
          </div>
        </div>
      )}

      <EmailPreviewConfirm
        open={!!emailPreview}
        onClose={() => setEmailPreview(null)}
        onConfirm={confirmEmail}
        confirming={emailSending}
        title="Raumbelegung — Voransicht"
        description="Bitte Begleittext prüfen. PDF-Anhang wird mitgesendet. Versand erst nach Bestätigung."
        to={emailPreview?.recipient}
        recipients={emailPreview?.recipients}
        subject={emailPreview?.subject}
        html={emailPreview?.html}
        attachmentNote={emailPreview?.attachment_note}
        sampleNote={emailPreview?.is_sample
          ? `Beispiel für 1 von ${emailPreview.eligible_count} Empfängern.`
          : null}
        confirmLabel={`An ${emailPreview?.eligible_count ?? 0} Empfänger senden`}
        testId="rf-email-preview"
      />

      {data.days.map(day => (
        <div key={day.day_of_week} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 font-semibold text-slate-800">
            {day.label === 'Mo' ? 'Montag' : day.label === 'Di' ? 'Dienstag' : day.label === 'Mi' ? 'Mittwoch' :
             day.label === 'Do' ? 'Donnerstag' : day.label === 'Fr' ? 'Freitag' : day.label === 'Sa' ? 'Samstag' : 'Sonntag'}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-slate-700 sticky left-0 bg-slate-50 min-w-[14rem]">Raum</th>
                  {data.slots.map(s => (
                    <th key={s} className="px-1 py-2 text-center font-mono text-slate-500 w-14">{s}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {day.rooms.map(r => (
                  <tr key={r.room_id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-4 py-2 sticky left-0 bg-white">
                      <button onClick={() => navigate(`/locations/${r.location_id}`)} className="text-left group">
                        <div className="font-medium text-slate-800 group-hover:text-brand-red">{r.room_name}</div>
                        <div className="text-[10px] text-slate-500 truncate">{r.location_name}</div>
                      </button>
                    </td>
                    {r.slots.map((s, i) => (
                      <td key={i} className="text-center p-0.5">
                        {s.status === 'free' ? (
                          <div className="w-full h-7 rounded bg-emerald-100 hover:bg-emerald-200 transition-colors" title="Frei" />
                        ) : (
                          <div
                            className="w-full h-7 rounded bg-red-200 hover:bg-red-300 transition-colors flex items-center justify-center text-[10px] text-red-900 cursor-help truncate px-1"
                            title={`Belegt: ${compactLessonLabel(formatClocks(s.course_name) || s.course_name || '')}${s.teacher ? ' · ' + s.teacher : ''} (${formatTime(s.start_time)} + ${s.duration_minutes}m)`}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
      <div className="text-xs text-slate-500 flex items-center gap-4">
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-emerald-100 rounded" /> Frei</span>
        <span className="flex items-center gap-1"><div className="w-3 h-3 bg-red-200 rounded" /> Belegt (Mouseover für Details)</span>
      </div>
    </div>
  );
}
